import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { reservationAPI, preferencesAPI, snapshotAPI } from '../api/client';
import { useToastStore } from '../store/toastStore';
import { track } from '../utils/analytics';
import Layout from '../components/Layout';
import SegmentControl from '../components/SegmentControl';
import BusSeatLayout from '../components/BusSeatLayout';
import BottomSheetSelect from '../components/BottomSheetSelect';

export default function QuickReserve() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const showToast = useToastStore(s => s.showToast);

  const userId = user?.userId;

  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const [routes, setRoutes] = useState([]);
  const [timetable, setTimetable] = useState([]);
  const [stops, setStops] = useState([]);
  const [selectedBus, setSelectedBus] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState([]);

  const [formData, setFormData] = useState({
    direction: 'DOWN',
    routeName: '',
    lineGroupSeq: '',
    busTime: '',
    busSeq: '',
    stopName: '',
    stopSeq: '',
  });

  // 방향별 상태 캐시
  const directionCache = useRef({ UP: null, DOWN: null });

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  // ── Snapshot query ──────────────────────────────────────────────────────────
  const {
    data: snapshotData,
    isLoading: loadingSnapshot,
  } = useQuery({
    queryKey: ['snapshot'],
    queryFn: async () => {
      const result = await snapshotAPI.getLatest();
      if (result.success && result.data) {
        return result.data;
      }
      throw new Error('노선 정보를 불러올 수 없습니다. 잠시 후 다시 시도해주세요.');
    },
    staleTime: 5 * 60 * 1000, // 5 minutes — snapshot changes infrequently
    retry: 1,
  });

  const snapshot = snapshotData || null;

  // ── Seat query with 1-second polling ───────────────────────────────────────
  const {
    data: seatsData,
    isLoading: loadingSeats,
  } = useQuery({
    queryKey: ['seats', formData.busSeq],
    queryFn: async () => {
      const res = await reservationAPI.getSeats(userId, formData.busSeq);
      if (res.success) {
        return res.data?.seats || [];
      }
      throw new Error('좌석 정보를 불러올 수 없습니다. 잠시 후 다시 시도해주세요.');
    },
    enabled: !!formData.busSeq && !!userId,
    refetchInterval: 1000,
    // Keep previous data visible while refetching (silent background update)
    placeholderData: (prev) => prev,
  });

  const seats = seatsData || [];

  // ── Sync preferences from DB (fire-and-forget, side-effect only) ───────────
  useEffect(() => {
    if (!userId) return;
    preferencesAPI.get(userId).then((result) => {
      if (result.success && result.data) {
        const { last_stop_seq, last_stop_name, last_direction, last_route_name } = result.data;
        if (last_stop_seq && last_stop_name) {
          const lastStop = {
            seq: last_stop_seq,
            name: last_stop_name,
            direction: last_direction,
            routeName: last_route_name,
          };
          localStorage.setItem('lastStop', JSON.stringify(lastStop));
        }
      }
    }).catch((err) => {
      console.error('Failed to sync preferences from DB:', err);
    });
  }, [userId]);

  // ── Snapshot error propagation ─────────────────────────────────────────────
  useEffect(() => {
    if (!snapshotData && !loadingSnapshot) {
      // query error is handled below via onError alternative — set via queryError
    }
  }, [snapshotData, loadingSnapshot]);

  // ── Derive routes from snapshot ────────────────────────────────────────────
  useEffect(() => {
    if (snapshot && formData.direction) {
      try {
        if (!snapshot.routes_data) {
          setRoutes([]);
          return;
        }
        const routeData = snapshot.routes_data[formData.direction] || [];
        const transformedRoutes = routeData.map(route => ({
          seq: route.lineGroupSeq,
          name: route.groupName,
          busCnt: route.busCnt,
        }));
        setRoutes(transformedRoutes);
      } catch (err) {
        console.error('노선 데이터 파싱 실패:', err);
        setError('노선 데이터 파싱 실패: ' + err.message);
      }
    }
  }, [snapshot, formData.direction]);

  // ── Derive timetable/stops from snapshot, then live-update ─────────────────
  useEffect(() => {
    if (!snapshot || !formData.lineGroupSeq || !formData.direction) return;

    const loadTimetable = async () => {
      try {
        if (!snapshot.routes_data) {
          setTimetable([]);
          setStops([]);
          return;
        }

        const routeData = snapshot.routes_data[formData.direction] || [];
        const selectedRoute = routeData.find(route => route.lineGroupSeq === formData.lineGroupSeq);

        if (!selectedRoute) {
          setTimetable([]);
          setStops([]);
          return;
        }

        // 1단계: 스냅샷 데이터로 즉시 표시
        const transformedTimetable = (selectedRoute.busList || []).map(bus => ({
          time: bus.operateTime,
          remainingSeats: bus.seatCount - bus.appCount,
          readyCount: bus.readyCount || 0,
          seq: bus.busSeq,
          carNum: bus.carNum,
        }));

        const transformedStops = (selectedRoute.stopList || []).map(stop => ({
          seq: stop.seq,
          stopName: stop.stopName,
        }));

        setTimetable(transformedTimetable);
        setStops(transformedStops);

        // 2단계: 백그라운드에서 실시간 데이터 가져와서 업데이트
        try {
          const res = await reservationAPI.getTimetable(
            userId,
            formData.direction,
            formData.lineGroupSeq
          );

          if (res.success && res.data?.timetable) {
            setTimetable(res.data.timetable);
            console.log('[QuickReserve] 시간표 실시간 업데이트 완료');
          }
        } catch (err) {
          console.error('[QuickReserve] 실시간 시간표 업데이트 실패:', err);
        }
      } catch (err) {
        console.error('시간표 데이터 파싱 실패:', err);
        setError('시간표 데이터 파싱 실패: ' + err.message);
      }
    };

    loadTimetable();
  }, [snapshot, formData.lineGroupSeq, formData.direction]);

  // ── Auto-select last stop when stops load ──────────────────────────────────
  useEffect(() => {
    if (stops.length > 0 && !formData.stopSeq) {
      const savedStop = localStorage.getItem('lastStop');
      if (savedStop) {
        try {
          const lastStop = JSON.parse(savedStop);
          if (
            lastStop.direction === formData.direction &&
            lastStop.routeName === formData.routeName
          ) {
            const matchingStop = stops.find(s => s.seq.toString() === lastStop.seq);
            if (matchingStop) {
              setFormData(prev => ({
                ...prev,
                stopSeq: matchingStop.seq.toString(),
                stopName: matchingStop.stopName,
              }));
            }
          }
        } catch (err) {
          console.error('Failed to parse lastStop:', err);
        }
      }
    }
  }, [stops]);

  // ── Reservation mutation ───────────────────────────────────────────────────
  const reserveMutation = useMutation({
    mutationFn: (data) => reservationAPI.execute(data),
    onMutate: () => {
      setError(null);
      setResult(null);
    },
    onSuccess: async (res) => {
      setResult(res);
      if (res.success) {
        track('reservation-executed', { direction: formData.direction, seatNo: formData.busSeq ? selectedSeats[0] : null });
        showToast('예약이 완료되었습니다!', 'success');
        setSelectedSeats([]);

        const lastStop = {
          seq: formData.stopSeq,
          name: formData.stopName,
          direction: formData.direction,
          routeName: formData.routeName,
        };
        localStorage.setItem('lastStop', JSON.stringify(lastStop));

        preferencesAPI.update({
          userId,
          lastStopSeq: formData.stopSeq,
          lastStopName: formData.stopName,
          lastDirection: formData.direction,
          lastRouteName: formData.routeName,
        }).catch(err => {
          console.error('Failed to save last stop to DB:', err);
        });
      } else {
        setError(res.error || '예약에 실패했습니다. 잠시 후 다시 시도해주세요.');
      }
    },
    onError: (err) => {
      const errorMsg = err.response?.data?.error || '예약에 실패했습니다. 잠시 후 다시 시도해주세요.';
      track('reservation-failed', { error: errorMsg });
      setError(errorMsg);
      setResult({ success: false, error: errorMsg });
    },
  });

  const handleRouteChange = (e) => {
    const selected = routes.find((r) => r.seq === e.target.value);
    if (selected) {
      setFormData({
        ...formData,
        lineGroupSeq: selected.seq,
        routeName: selected.name,
        busTime: '',
        busSeq: '',
        stopName: '',
        stopSeq: '',
      });
      setTimetable([]);
      setStops([]);
      setSelectedBus(null);
      setSelectedSeats([]);
    }
  };

  const handleBusChange = (bus) => {
    setSelectedBus(bus);
    setFormData({
      ...formData,
      busTime: bus.time,
      busSeq: bus.seq,
    });
    setSelectedSeats([]);
  };

  const handleStopChange = (e) => {
    const selected = stops.find((s) => s.seq.toString() === e.target.value);
    if (selected) {
      setFormData({
        ...formData,
        stopSeq: selected.seq.toString(),
        stopName: selected.stopName,
      });
    }
  };

  const handleSeatClick = (seat) => {
    if (!seat.available) return;

    const seatNumber = seat.number;
    if (selectedSeats.includes(seatNumber)) {
      setSelectedSeats(selectedSeats.filter((s) => s !== seatNumber));
    } else {
      setSelectedSeats([seatNumber]); // 단일 좌석만 선택 가능
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedSeats.length === 0) {
      showToast('좌석을 선택해주세요', 'error');
      return;
    }

    reserveMutation.mutate({
      userId,
      busSeq: formData.busSeq,
      seatNo: selectedSeats[0],
      stopSeq: parseInt(formData.stopSeq),
    });
  };

  if (!user) return null;

  return (
    <Layout title="버스 예약">
      <div className="max-w-lg mx-auto space-y-5">
        {/* Snapshot loading */}
        {loadingSnapshot && !snapshot && (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-tayo/20 border-t-tayo rounded-full animate-spin" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-coral/10 border border-coral/20 rounded-xl p-4 animate-fade-up">
            <p className="text-sm text-coral font-medium">{error}</p>
          </div>
        )}

        {/* Section A: 어디로 가시나요? */}
        {snapshot && (
          <div className="bg-white rounded-2xl shadow-card p-5 animate-fade-up">
            <h3 className="text-lg font-bold text-charcoal mb-4">어디로 가시나요?</h3>

            <SegmentControl
              options={[
                { value: 'UP', label: '등교' },
                { value: 'DOWN', label: '하교' },
              ]}
              value={formData.direction}
              onChange={(val) => {
                // 현재 방향 상태 저장
                directionCache.current[formData.direction] = {
                  formData: { ...formData },
                  timetable,
                  stops,
                  seats,
                  selectedBus,
                  selectedSeats,
                };

                // 새 방향의 캐시 복원 또는 초기화
                const cached = directionCache.current[val];
                if (cached) {
                  setFormData({ ...cached.formData, direction: val });
                  setTimetable(cached.timetable);
                  setStops(cached.stops);
                  setSelectedBus(cached.selectedBus);
                  setSelectedSeats(cached.selectedSeats);
                } else {
                  setFormData({
                    direction: val,
                    routeName: '',
                    lineGroupSeq: '',
                    busTime: '',
                    busSeq: '',
                    stopName: '',
                    stopSeq: '',
                  });
                  setTimetable([]);
                  setStops([]);
                  setSelectedBus(null);
                  setSelectedSeats([]);
                }
              }}
            />

            {/* 노선 */}
            {routes.length > 0 && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-charcoal mb-2">노선</label>
                <BottomSheetSelect
                  value={formData.lineGroupSeq}
                  onChange={handleRouteChange}
                  options={routes.map(r => ({ value: r.seq, label: r.name, sublabel: `${r.busCnt}대` }))}
                  placeholder="노선을 선택하세요"
                  title="노선 선택"
                  searchable={routes.length > 5}
                  disabled={loadingSnapshot}
                />
              </div>
            )}

            {/* 정류장 */}
            {stops.length > 0 && (
              <div className="mt-4 animate-slide-down">
                <label className="block text-sm font-medium text-charcoal mb-2">정류장</label>
                <BottomSheetSelect
                  value={formData.stopSeq}
                  onChange={handleStopChange}
                  options={stops.map(s => ({ value: s.seq, label: s.stopName }))}
                  placeholder="정류장을 선택하세요"
                  title="정류장 선택"
                  searchable={stops.length > 5}
                />
              </div>
            )}
          </div>
        )}

        {/* Section B: 언제 타시나요? */}
        {formData.lineGroupSeq && (
          <div className="bg-white rounded-2xl shadow-card p-5 animate-slide-down">
            <h3 className="text-lg font-bold text-charcoal mb-4">언제 타시나요?</h3>
            {timetable.length === 0 ? (
              <p className="text-sub-text text-sm">시간표를 불러오는 중...</p>
            ) : (
              <BottomSheetSelect
                value={formData.busSeq}
                onChange={(e) => {
                  const bus = timetable.find(b => String(b.seq) === e.target.value);
                  if (bus) handleBusChange(bus);
                }}
                options={timetable.map(bus => {
                  const s = bus.remainingSeats;
                  const w = bus.readyCount || 0;
                  const sublabelColor = s === 0 ? 'text-sub-text' : s <= 10 ? 'text-coral' : s <= 22 ? 'text-amber-500' : 'text-mint';
                  return {
                    value: bus.seq,
                    label: bus.time,
                    sublabel: s > 0 ? `${s}석` : w > 0 ? `만석 · 대기 ${w}명` : '만석',
                    sublabelColor,
                  };
                })}
                placeholder="시간을 선택하세요"
                title="시간 선택"
                searchable={timetable.length > 10}
              />
            )}
          </div>
        )}

        {/* Section C: 어디 앉으시겠어요? */}
        {formData.busSeq && (
          <div className="bg-white rounded-2xl shadow-card p-5 animate-slide-down">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-charcoal">어디 앉으시겠어요?</h3>
              {selectedBus && (
                <span className="text-xs text-sub-text bg-gray-100 px-2.5 py-1 rounded-full">
                  {selectedBus.time}
                </span>
              )}
            </div>

            {loadingSeats ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-2 border-tayo/20 border-t-tayo rounded-full animate-spin" />
              </div>
            ) : seats.length === 0 ? (
              <p className="text-sub-text text-center py-4">좌석 정보가 없습니다</p>
            ) : (
              <BusSeatLayout
                seats={seats}
                selectedSeats={selectedSeats}
                onSeatClick={(seatNum) => {
                  const seat = seats.find(s => s.number === seatNum);
                  if (seat) handleSeatClick(seat);
                }}
                mode="reserve"
                totalSeats={seats.length}
              />
            )}
          </div>
        )}

        {/* Spacer for sticky CTA */}
        {selectedSeats.length > 0 && formData.stopSeq && <div className="h-20" />}
      </div>

      {/* Sticky CTA — above BottomNav */}
      {selectedSeats.length > 0 && formData.stopSeq && (
        <div className="fixed bottom-[4.5rem] left-0 right-0 px-4 pb-3 pt-3 bg-gradient-to-t from-snow via-snow to-transparent z-40">
          <div className="max-w-lg mx-auto">
            <button
              onClick={(e) => handleSubmit(e)}
              disabled={reserveMutation.isPending}
              className="w-full py-4 bg-gradient-to-r from-tayo to-tayo-dark text-white rounded-xl font-bold text-base btn-press disabled:opacity-50 shadow-lg transition-all"
            >
              {reserveMutation.isPending ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  예약 중...
                </span>
              ) : `${selectedSeats[0]}번 좌석 예약하기`}
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
}
